import React, { useCallback, useEffect, useState } from "react";
import { attendanceApi, productsApi } from "../../services/api.service";
import { T, SectionLabel, Field, Input, Select, Btn } from "../shared/UI";

function localDateString(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function AgentAttendanceReportView() {
  const today = localDateString();
  const [fromDate, setFromDate] = useState(() => localDateString());
  const [toDate, setToDate] = useState(() => localDateString());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");

  const clampToToday = useCallback(
    (dateStr) => {
      if (!dateStr) return today;
      if (dateStr > today) return today;
      return dateStr;
    },
    [today]
  );

  const onFromChange = (e) => {
    const v = clampToToday(e.target.value);
    setFromDate(v);
    setToDate((t) => (t < v ? v : clampToToday(t)));
    setResult(null);
    setError(null);
  };

  const onToChange = (e) => {
    const v = clampToToday(e.target.value);
    setToDate(v < fromDate ? fromDate : v);
    setResult(null);
    setError(null);
  };

  const resetToToday = () => {
    setFromDate(today);
    setToDate(today);
    setProductId("");
    setResult(null);
    setError(null);
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await productsApi.list();
        const raw = Array.isArray(res.data?.data) ? res.data.data : [];
        if (mounted) setProducts(raw);
      } catch {
        if (mounted) setProducts([]);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const generateExcel = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await attendanceApi.agentsExcel(fromDate, toDate, productId);
      const data = res.data;
      if (data?.status && data?.url) {
        setResult({ url: data.url, count: data.count, message: data.message });
      } else {
        setError(data?.message || "Could not generate attendance Excel.");
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Request failed";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mobile-padding" style={styles.wrap}>
      <div style={styles.toolbar}>
        <SectionLabel>AGENT ATTENDANCE REPORT</SectionLabel>
        <p style={styles.hint}>
          Choose a date range (up to today). Generates an Excel file on the server; use the link below to download.
        </p>
      </div>

      <div style={styles.card}>
        <div style={styles.row}>
          <Field label="FROM DATE">
            <Input
              type="date"
              value={fromDate}
              max={today}
              onChange={onFromChange}
            />
          </Field>
          <Field label="TO DATE">
            <Input
              type="date"
              value={toDate}
              min={fromDate}
              max={today}
              onChange={onToChange}
            />
          </Field>
          <Field label="PRODUCT">
            <Select
              value={productId}
              onChange={(e) => {
                setProductId(e.target.value);
                setResult(null);
                setError(null);
              }}
            >
              <option value="">All Products</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </Select>
          </Field>
        </div>
        <div style={styles.actions}>
          <Btn variant="ghost" onClick={resetToToday}>
            Today only
          </Btn>
          <Btn onClick={generateExcel} disabled={loading}>
            {loading ? "Generating…" : "Generate Excel"}
          </Btn>
        </div>
      </div>

      {error && (
        <div style={{ ...styles.banner, borderColor: T.sos, color: T.sos }}>{error}</div>
      )}

      {result && (
        <div style={styles.successCard}>
          <div style={styles.successTitle}>{result.message || "Attendance Excel generated"}</div>
          {result.count != null && (
            <div style={styles.count}>
              Records: <strong>{result.count}</strong>
            </div>
          )}
          <div style={{ marginTop: 12 }}>
            <Btn
              variant="outline"
              onClick={() => window.open(result.url, "_blank", "noopener,noreferrer")}
            >
              Open / Download Excel
            </Btn>
          </div>
          <div style={styles.urlNote}>
            <span style={{ color: T.muted, fontSize: 11 }}>URL expires per S3 policy — download soon if needed.</span>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  wrap: { flex: 1, overflowY: "auto", padding: 20, display: "flex", flexDirection: "column", gap: 16 },
  toolbar: { display: "flex", flexDirection: "column", gap: 8 },
  hint: { margin: 0, fontSize: 12, color: T.muted, maxWidth: 560, lineHeight: 1.5 },
  card: {
    background: T.s2,
    border: `1px solid ${T.border}`,
    borderRadius: 12,
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 16,
    maxWidth: 520,
  },
  row: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 16 },
  actions: { display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" },
  banner: {
    padding: "12px 14px",
    borderRadius: 8,
    border: `1px solid`,
    fontSize: 13,
    maxWidth: 560,
  },
  successCard: {
    background: "rgba(106, 247, 196, 0.06)",
    border: `1px solid rgba(106, 247, 196, 0.25)`,
    borderRadius: 12,
    padding: 16,
    maxWidth: 560,
  },
  successTitle: { fontSize: 14, fontWeight: 600, color: T.text },
  count: { marginTop: 8, fontSize: 13, color: T.muted },
  urlNote: { marginTop: 10 },
};
